// WikiInteractions.jsx — admin AI-interaction log: metrics, table, detail drawer + training actions.
import React, { useEffect, useState, useCallback } from 'react';
import {
    MessagesSquare, FileText, Check, HelpCircle, Flag, X, GraduationCap, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { useWikiApi } from '../../../hooks/useWiki';
import { DEPT_BY_ID, relativeTime } from '../../../lib/wikiConstants';
import WikiCiteChip from '../WikiCiteChip';

const STATUS = {
    answered: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Check, label: 'Answered' },
    gap: { cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: HelpCircle, label: 'Knowledge gap' },
    flagged: { cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: Flag, label: 'Flagged' },
};
const PALETTE = ['bg-stone-900', 'bg-rose-500', 'bg-emerald-600', 'bg-stone-900', 'bg-amber-500', 'bg-cyan-600'];
function Avatar({ name, size = 22 }) {
    const initials = (name || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
    return <div className={`${PALETTE[(name || '').length % PALETTE.length]} rounded-full text-white font-semibold flex items-center justify-center shrink-0`} style={{ width: size, height: size, fontSize: size * 0.4 }}>{initials}</div>;
}

function Metric({ label, value, sub, tone = 'text-stone-900' }) {
    return (
        <div className="bg-white rounded-xl border border-stone-200 p-3.5 flex-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">{label}</div>
            <div className={`wiki-display text-[22px] font-bold mt-1 leading-none ${tone}`}>{value}</div>
            {sub && <div className="text-[11px] text-stone-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function WikiInteractions({ onOpenDoc }) {
    const api = useWikiApi();
    const [dept, setDept] = useState('all');
    const [data, setData] = useState(null);
    const [picked, setPicked] = useState(null);

    const load = useCallback(() => { api.adminInteractions(dept, 'all').then(setData).catch(() => setData({ interactions: [], metrics: {} })); }, [api, dept]);
    useEffect(() => { load(); }, [load]);

    const rows = data?.interactions || [];
    const m = data?.metrics || {};

    return (
        <div className="h-full flex">
            <div className="flex-1 overflow-auto p-5">
                <div className="flex gap-3 mb-4">
                    <Metric label="Questions (7d)" value={m.questions_7d ?? 0} sub="last 7 days" />
                    <Metric label="Answer rate" value={`${m.answer_rate ?? 0}%`} sub={`${m.answered_count ?? 0} answered`} tone="text-emerald-600" />
                    <Metric label="Helpful" value={`${m.helpful_pct ?? 0}%`} sub={`${m.ratings ?? 0} ratings`} />
                    <Metric label="Knowledge gaps" value={m.knowledge_gaps ?? 0} sub="needs an owner" tone="text-amber-600" />
                </div>

                <div className="bg-white rounded-xl border border-stone-200 wiki-shadow-soft overflow-hidden">
                    <div className="px-4 h-12 flex items-center gap-2 border-b border-stone-200">
                        <MessagesSquare className="w-4 h-4 text-stone-500" />
                        <span className="text-[13px] wiki-display font-bold text-stone-900">Agent interactions</span>
                        <div className="flex-1" />
                        <div className="flex gap-1 p-0.5 bg-stone-100 rounded-md">
                            {[['all', 'All'], ['marketing', 'Marketing'], ['sales', 'Sales'], ['service', 'Service'], ['company', 'Company']].map(([k, l]) => (
                                <button key={k} onClick={() => setDept(k)} className={`px-2.5 py-1 rounded text-[11px] font-semibold ${dept === k ? 'bg-white wiki-shadow-soft text-stone-900' : 'text-stone-500 hover:text-stone-900'}`}>{l}</button>
                            ))}
                        </div>
                    </div>
                    <table className="w-full text-[13px]">
                        <thead className="bg-stone-100/60 text-[10px] uppercase tracking-wider text-stone-500">
                            <tr>{['User', 'Question', 'Agent', 'Sources', 'Status', 'When'].map(h => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
                        </thead>
                        <tbody>
                            {rows.map(l => {
                                const s = STATUS[l.status] || STATUS.answered;
                                const d = DEPT_BY_ID[(l.depts || [])[0]];
                                const firstCite = (l.cited_pages || [])[0];
                                return (
                                    <tr key={l.interaction_id} onClick={() => setPicked(l)} className="border-t border-stone-200 hover:bg-stone-50 cursor-pointer">
                                        <td className="px-4 py-2.5"><div className="flex items-center gap-2"><Avatar name={l.user_name} /><span className="text-stone-900 font-medium whitespace-nowrap">{l.user_name}</span></div></td>
                                        <td className="px-4 py-2.5 text-stone-600 max-w-[280px] truncate">{l.question}</td>
                                        <td className="px-4 py-2.5"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 capitalize">{d?.name || '—'}</span></td>
                                        <td className="px-4 py-2.5">{firstCite ? <button onClick={e => { e.stopPropagation(); onOpenDoc(firstCite.page_id); }} className="text-stone-900 hover:underline inline-flex items-center gap-1 text-[12px]"><FileText className="w-3 h-3" />{firstCite.name}</button> : <span className="text-stone-400">—</span>}</td>
                                        <td className="px-4 py-2.5"><span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.cls}`}><s.icon className="w-3 h-3" />{s.label}</span></td>
                                        <td className="px-4 py-2.5 text-stone-500 whitespace-nowrap">{relativeTime(l.created_at)}</td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-stone-400">No interactions yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
            {picked && <LogDrawer log={picked} onClose={() => setPicked(null)} onOpenDoc={onOpenDoc} onTrained={load} />}
        </div>
    );
}

function LogDrawer({ log, onClose, onOpenDoc, onTrained }) {
    const api = useWikiApi();
    const s = STATUS[log.status] || STATUS.answered;
    const [adding, setAdding] = useState(false);
    const [answer, setAnswer] = useState('');
    const cites = log.cited_pages || [];

    const addCanonical = async () => {
        try {
            const t = await api.getTraining();
            const canon = (t.training?.canonical || []).map(c => ({ canon_id: c.canon_id, dept: c.dept, q: c.q, a: c.a, source_page_id: c.source_page_id }));
            canon.push({ dept: (log.depts || [])[0], q: log.question, a: answer, source_page_id: cites[0]?.page_id || null });
            await api.setTraining({ canonical: canon });
            toast.success('Canonical answer added'); setAdding(false); setAnswer(''); onTrained && onTrained();
        } catch { toast.error('Could not save'); }
    };
    const excludePage = async () => {
        try {
            const t = await api.getTraining();
            const ex = new Set(t.training?.excluded_page_ids || []);
            cites.forEach(c => ex.add(c.page_id));
            await api.setTraining({ excluded_page_ids: [...ex] });
            toast.success('Page excluded from agent'); onTrained && onTrained();
        } catch { toast.error('Could not save'); }
    };

    return (
        <div className="w-[360px] border-l border-stone-200 bg-white flex flex-col h-full">
            <div className="h-12 px-4 flex items-center gap-2 border-b border-stone-200">
                <span className="text-[13px] wiki-display font-bold text-stone-900 flex-1">Interaction</span>
                <button onClick={onClose} className="w-7 h-7 rounded hover:bg-stone-100 text-stone-500 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
                <div className="flex items-center gap-2"><Avatar name={log.user_name} size={28} /><div><div className="text-[12px] font-semibold text-stone-900">{log.user_name}</div><div className="text-[11px] text-stone-500">{relativeTime(log.created_at)}</div></div><span className={`ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.cls}`}><s.icon className="w-3 h-3" />{s.label}</span></div>
                <div className="rounded-lg bg-stone-900 text-white px-3 py-2 text-[13px]">{log.question}</div>
                <div className="rounded-lg border border-stone-200 px-3 py-2.5 text-[13px] text-stone-600 leading-relaxed whitespace-pre-line">{log.answer || '—'}</div>
                {cites.length > 0 && <div><div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1.5">Sources cited</div><div className="flex flex-wrap gap-1.5">{cites.map((c, i) => <WikiCiteChip key={i} cite={c} onOpenDoc={onOpenDoc} />)}</div></div>}
                <div className="pt-2 border-t border-stone-200 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">Train from this</div>
                    {adding ? (
                        <div className="space-y-2">
                            <textarea value={answer} onChange={e => setAnswer(e.target.value)} placeholder="Write the canonical answer…" className="w-full h-24 rounded-lg border border-stone-200 p-2 text-[13px] resize-none focus:outline-none focus:border-stone-400" />
                            <div className="flex gap-2"><button onClick={addCanonical} disabled={!answer.trim()} className="flex-1 h-8 rounded-lg wiki-grad text-white text-[12px] font-semibold disabled:opacity-50">Save</button><button onClick={() => setAdding(false)} className="h-8 px-3 rounded-lg border border-stone-200 text-[12px] font-semibold">Cancel</button></div>
                        </div>
                    ) : (
                        <button onClick={() => setAdding(true)} className="w-full h-9 rounded-lg wiki-grad text-white text-[12px] font-semibold inline-flex items-center justify-center gap-1.5"><GraduationCap className="w-4 h-4" /> Add canonical answer</button>
                    )}
                    {cites.length > 0 && <button onClick={excludePage} className="w-full h-9 rounded-lg border border-rose-200 text-rose-600 text-[12px] font-semibold inline-flex items-center justify-center gap-1.5"><EyeOff className="w-4 h-4" /> Exclude cited page from agent</button>}
                </div>
            </div>
        </div>
    );
}
